import React, { Fragment } from "react";

import { Container, Row } from "../components";
import { OrderStatus } from "../data";

import { useI18n } from "../lib/i18n/i18n_provider";
import {
  BookingType,
  OrderItem,
  PromotionLine,
  Quote,
  TaxLine,
  useSpaceBookingPolicyQuery,
} from "./api";

import { Divider } from "./divider";
import { Hidden } from "./hidden";
import { OrderParams } from "./order_params";
import { Spacer } from "./spacer";
import { Text } from "./text";
import { getDurationInHours } from "./time_utils";

import { getFieldFromObject } from "../offering_v2/utils/get_field_from_object";
import { getCheckoutCancellationPolicyText } from "./get_checkout_cancellation_policy_text";
import {
  getStartDateFromOrderParams,
  getEndDateFromOrderParams,
} from "./order_params_utils";

interface QuoteViewProps {
  orderParams: OrderParams | undefined;
  locationTimezone: string;
  quote: Quote;
  includeQuantity?: boolean;
  currency: string | null;
  pricingInclusion: string | null;
  orderStatus?: OrderStatus;
}

export function QuoteView(props: QuoteViewProps) {
  const {
    orderParams,
    locationTimezone,
    quote,
    currency,
    includeQuantity,
    pricingInclusion,
    orderStatus,
  } = props;
  const { fbt, locale } = useI18n();
  const { data: bookingPolicy } = useSpaceBookingPolicyQuery(
    orderParams?.spaceID,
    getStartDateFromOrderParams(orderParams),
    getEndDateFromOrderParams(orderParams)
  );

  const quoteBoxMessage = getCheckoutCancellationPolicyText(
    fbt,
    locationTimezone,
    bookingPolicy,
    orderParams,
    locale,
    quote
  );

  return (
    <Container>
      <OrderItemsView
        includeQuantity={includeQuantity}
        orderItems={quote.orderItems}
        taxLine={quote.taxLine}
        currency={currency}
        promotionLine={quote.promotionLine}
      />
      {quote.promotionLine && (
        <Fragment>
          <Spacer size={4} />
          <PromotionLineItemView
            promotionLine={quote.promotionLine}
            currency={currency}
          />
        </Fragment>
      )}
      {quote.taxLine && (
        <Fragment>
          <Spacer size={16} />
          <Row justifyContent="space-between">
            <Text>{fbt("Subtotal")}</Text>
            <Text>{fbt.formatCurrency(quote.subTotal, currency)}</Text>
          </Row>
          <Spacer size={4} />
          <TaxLineItemView taxLine={quote.taxLine} currency={currency} />
        </Fragment>
      )}
      <Spacer size={16} />
      <Row justifyContent="space-between">
        <Text>
          {fbt("Total")} {currency && `(${currency})`}
        </Text>
        <Text weight="bold">
          {fbt.formatCurrency(quote.totalPrice, currency)}
        </Text>
      </Row>
      {pricingInclusion && (
        <Fragment>
          <Spacer size={16} />
          <Row justifyContent="flex-end">
            <Text size="sm">{pricingInclusion}</Text>
          </Row>
        </Fragment>
      )}
      <Spacer size={16} />
      {orderStatus !== OrderStatus.Canceled && quoteBoxMessage && (
        <Fragment>
          <Divider />
          <Spacer size={8} />
          <Text size="sm">{quoteBoxMessage}</Text>
          <Spacer size={8} />
        </Fragment>
      )}
      <Hidden md>
        {!orderStatus && !orderParams?.email && (
          <Container>
            <Text size="sm" italic>
              {bookingPolicy?.checkoutWarning}
            </Text>
          </Container>
        )}
      </Hidden>
    </Container>
  );
}

interface OrderItemsViewProps {
  includeQuantity?: boolean;
  orderItems: OrderItem[];
  currency: string | null;
  taxLine: TaxLine | null;
  promotionLine?: PromotionLine | null;
}

export function OrderItemsView(props: OrderItemsViewProps) {
  const {
    orderItems,
    currency,
    includeQuantity = false,
    promotionLine,
    taxLine,
  } = props;

  return (
    <Fragment>
      {orderItems.map((orderItem, index) => {
        const details = getFieldFromObject("details", orderItem);
        const type = getFieldFromObject("type", details);
        if (type === BookingType.Daily) {
          return (
            <DailyBookingOrderItemView
              orderItem={orderItem}
              days={getFieldFromObject("days", details)}
              includeQuantity={includeQuantity}
              currency={currency}
              key={index}
            />
          );
        } else if (type === BookingType.Hourly) {
          return (
            <HourlyBookingOrderItemView
              startTime={getFieldFromObject("startTime", details)}
              endTime={getFieldFromObject("endTime", details)}
              orderItem={orderItem}
              currency={currency}
              key={index}
            />
          );
        } else {
          return (
            <AddOnOrderViewItem
              orderItem={orderItem}
              currency={currency}
              promotionLine={promotionLine}
              orderItems={orderItems}
              taxLine={taxLine}
              addOnName={getFieldFromObject("name", details)}
              key={index}
            />
          );
        }
      })}
    </Fragment>
  );
}

interface DailyOrderItemViewProps {
  includeQuantity?: boolean;
  orderItem: OrderItem;
  days: number;
  currency: string | null;
}

function DailyBookingOrderItemView(props: DailyOrderItemViewProps) {
  const { includeQuantity, orderItem, currency, days } = props;
  const quantity = orderItem.quantity;
  const { fbt } = useI18n();

  return (
    <Container key={orderItem.type}>
      <Spacer size={16} />
      <Row justifyContent="space-between">
        <Text>
          {fbt.formatCurrency(orderItem.unitPrice, currency)} *{" "}
          {fbt(
            fbt.plural("day", days, {
              name: "number of days",
              showCount: "yes",
              many: "days",
            })
          )}{" "}
          {includeQuantity && quantity ? `* ${quantity} ${fbt("QTY")}` : ""}
        </Text>
        <Text>{fbt.formatCurrency(orderItem.subTotal, currency)}</Text>
      </Row>
    </Container>
  );
}

interface HourlyOrderItemViewProps {
  orderItem: OrderItem;
  startTime: string;
  endTime: string;
  currency: string | null;
}

function HourlyBookingOrderItemView(props: HourlyOrderItemViewProps) {
  const { orderItem, currency, startTime, endTime } = props;
  const { fbt } = useI18n();
  const duration = getDurationInHours(startTime, endTime);
  return (
    <Container key={orderItem.type}>
      <Spacer size={16} />
      <Row justifyContent="space-between">
        <Text>
          {fbt.formatCurrency(orderItem.unitPrice, currency)} *{" "}
          {fbt(
            fbt.plural("hour", duration, {
              name: "number of hours",
              showCount: "yes",
              many: "hours",
            })
          )}
        </Text>
        <Text>{fbt.formatCurrency(orderItem.subTotal, currency)}</Text>
      </Row>
    </Container>
  );
}

interface AddOnOrderItemViewProps {
  orderItems: OrderItem[];
  orderItem: OrderItem;
  currency: string | null;
  taxLine: TaxLine | null;
  addOnName: string;
  promotionLine?: PromotionLine | null;
}

function AddOnOrderViewItem(props: AddOnOrderItemViewProps) {
  const { orderItems, orderItem, currency, promotionLine, taxLine, addOnName } =
    props;
  const { fbt } = useI18n();
  return (
    <Fragment>
      {orderItems.length === 1 && (promotionLine || taxLine) && (
        <Container key={orderItem.type}>
          <Spacer size={16} />
          <Row justifyContent="space-between">
            <Text>{addOnName}</Text>
            <Text>{fbt.formatCurrency(orderItem.subTotal, currency)}</Text>
          </Row>
        </Container>
      )}
      {orderItems.length > 1 && (
        <Container key={orderItem.type}>
          <Spacer size={16} />
          <Row justifyContent="space-between">
            <Text>{addOnName}</Text>
            <Text>{fbt.formatCurrency(orderItem.subTotal, currency)}</Text>
          </Row>
        </Container>
      )}
    </Fragment>
  );
}

export interface TaxLineItemViewProps {
  taxLine: TaxLine;
  currency: string | null;
}

export function TaxLineItemView(props: TaxLineItemViewProps) {
  const { taxLine, currency } = props;
  const { fbt } = useI18n();

  return (
    <Container>
      <Row justifyContent="space-between">
        <Text>{taxLine.description}</Text>
        <Text>{fbt.formatCurrency(taxLine.amount, currency)}</Text>
      </Row>
    </Container>
  );
}

export interface PromotionLineItemViewProps {
  promotionLine: PromotionLine;
  currency: string | null;
}

export function PromotionLineItemView(props: PromotionLineItemViewProps) {
  const { promotionLine, currency } = props;
  const { fbt } = useI18n();

  return (
    <Container>
      <Row justifyContent="space-between">
        <Text>{promotionLine.description}</Text>
        <Text nowrap>
          - {fbt.formatCurrency(Math.abs(promotionLine.amount), currency)}
        </Text>
      </Row>
    </Container>
  );
}
